import React, { useState, useRef } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import { Video, VideoOff, Send, X, Circle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const VideoRecorder = ({ onSend, onCancel }) => {
  const [recordingTime, setRecordingTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [sending, setSending] = useState(false);
  const videoPreviewRef = useRef(null);

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    previewStream,
    clearBlobUrl
  } = useReactMediaRecorder({
    video: true,
    audio: true,
    onStart: () => {
      const interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
      setTimerInterval(interval);
    },
    onStop: () => {
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
    }
  });

  React.useEffect(() => {
    if (previewStream && videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    setRecordingTime(0);
    startRecording();
  };

  const handleStop = () => {
    stopRecording();
  };

  const handleSend = async () => {
    if (!mediaBlobUrl || sending) return;
    setSending(true);

    try {
      const response = await fetch(mediaBlobUrl);
      const blob = await response.blob();
      const file = new File([blob], `video-${Date.now()}.webm`, { type: 'video/webm' });
      
      await onSend(file, recordingTime);
      clearBlobUrl();
      setRecordingTime(0);
    } catch (error) {
      toast.error('Failed to send video message');
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    clearBlobUrl();
    setRecordingTime(0);
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    if (onCancel) onCancel();
  };

  if (status === 'idle' || status === 'stopped') {
    return (
      <div className="flex flex-col gap-3 p-4 bg-white dark:bg-[#1E293B] border border-[#E2E8F0] dark:border-[#334155] rounded-md">
        {mediaBlobUrl ? (
          <>
            <video 
              src={mediaBlobUrl} 
              controls 
              className="w-full rounded-md max-h-64"
              data-testid="video-preview"
            />
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#475569] dark:text-[#94A3B8]">{formatTime(recordingTime)}</span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancel}
                  data-testid="video-cancel-button"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSend}
                  disabled={sending}
                  className="bg-[#4338CA] hover:bg-[#3730A3]"
                  data-testid="video-send-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </Button>
              </div>
            </div>
          </>
        ) : (
          <Button
            onClick={handleStart}
            className="flex items-center gap-2"
            data-testid="video-start-button"
          >
            <Video className="w-5 h-5" />
            Start Video Recording
          </Button>
        )}
      </div>
    );
  }

  if (status === 'recording') {
    return (
      <div className="flex flex-col gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md" data-testid="video-recording">
        <video
          ref={videoPreviewRef}
          autoPlay
          muted
          className="w-full rounded-md max-h-64 bg-black"
        />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Circle className="w-3 h-3 fill-red-500 text-red-500 animate-pulse" />
            <span className="text-sm font-medium text-red-700 dark:text-red-400">Recording...</span>
            <span className="text-sm text-red-600 dark:text-red-500">{formatTime(recordingTime)}</span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleCancel}
              data-testid="video-cancel-recording-button"
            >
              <X className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleStop}
              data-testid="video-stop-button"
            >
              <VideoOff className="w-4 h-4 mr-2" />
              Stop
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default VideoRecorder;
